import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Search, ArrowLeft, Plus, Trash2, FileText, Download, Save, Upload, Filter, X, RefreshCw, Eye, EyeOff, MessageCircle, Zap, Copy, Link2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import BackToPanelButton, { redirectToHubIfFromHub } from '../components/BackToPanelButton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  commercialLogin, commercialLogout, commercialMe,
  getCommercialTalentos, createCommercialTalento,
  listPropuestas, getPropuesta, createPropuesta, updatePropuesta, deletePropuesta,
} from '../services/commercial';
import PropuestaPreview, { computeScopeFromTalentos, parseValue, parseQty, formatValue } from '../components/PropuestaPreview';
import { generateProposalPDF } from '../services/pdfGenerator';

// Opciones para la tabla de precios estructurada
const REDES_OPTIONS = ['Instagram', 'TikTok', 'YouTube', 'Otros'];
const TIPO_OPTIONS = ['Reel', 'Reel colab', 'Pauta', 'Reel c/repost', 'TikTok', 'Posteo', 'YouTube Short', 'Otros'];
const EMPTY_PRICE_ITEM = () => ({ red: '', cantidad: '', tipo: '', precio: '', notas: '' });

// ============ LOGIN SCREEN ============
const CommercialLogin = ({ onAuthed, onBack }) => {
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      await commercialLogin(password, remember);
      onAuthed();
    } catch (e2) {
      const s = e2?.response?.status;
      if (s === 401) setErr('Contraseña incorrecta');
      else if (s === 429) setErr('Demasiados intentos');
      else setErr(e2?.response?.data?.detail || 'Error de red');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
      <form onSubmit={submit} className="w-full max-w-sm p-8" data-testid="commercial-login-form">
        <button type="button" onClick={onBack} className="text-gray-500 hover:text-gray-300 text-xs mb-6 flex items-center gap-1" data-testid="commercial-login-back">
          <ArrowLeft size={12} /> Volver al Panel
        </button>
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F4A261' }}>
            <FileText size={28} className="text-white" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-white text-center mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          Presupuestos
        </h2>
        <p className="text-xs text-gray-500 text-center mb-6">Acceso comercial · Talengers</p>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Contraseña comercial"
          autoComplete="current-password"
          className="bg-gray-900 border-gray-700 text-white mb-3"
          data-testid="commercial-login-password"
        />
        <label className="flex items-center gap-2 mb-3 text-xs text-gray-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="accent-orange-500 h-4 w-4"
            data-testid="commercial-login-remember"
          />
          Recordarme 7 días en este dispositivo
        </label>
        {err && <p className="text-red-400 text-sm mb-3 text-center" data-testid="commercial-login-error">{err}</p>}
        <Button
          type="submit"
          disabled={busy || !password}
          className="w-full text-white font-semibold"
          style={{ backgroundColor: '#F4A261' }}
          data-testid="commercial-login-submit"
        >
          {busy ? 'Verificando...' : 'Ingresar'}
        </Button>
      </form>
    </div>
  );
};

// ============ CATÁLOGO SELECTOR ============
const CatalogSelector = ({ open, onClose, onConfirm, existingIds = [] }) => {
  const [talentos, setTalentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [visFilter, setVisFilter] = useState('__ALL__');
  const [verticalFilter, setVerticalFilter] = useState('__ALL__');
  const [paisFilter, setPaisFilter] = useState('__ALL__');
  const [tipoCampanaFilter, setTipoCampanaFilter] = useState('__ALL__');
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setLoading(true);
    getCommercialTalentos()
      .then((d) => setTalentos(d.talentos || []))
      .catch(() => setTalentos([]))
      .finally(() => setLoading(false));
  }, [open]);

  // Helper: split multi-valor (verticales, países, tipos) por coma + trim.
  const splitMulti = (raw) => String(raw || '').split(',').map(v => v.trim()).filter(Boolean);
  // Helper: dedup lista manteniendo el primer casing visto (case-insensitive).
  const dedupCi = (arr) => {
    const seen = new Map();  // lower → original
    arr.forEach(v => { const k = v.toLowerCase(); if (!seen.has(k)) seen.set(k, v); });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  };

  const verticales = useMemo(() => {
    const all = [];
    talentos.forEach((t) => all.push(...splitMulti(t.vertical)));
    return dedupCi(all);
  }, [talentos]);

  const paisesResidencia = useMemo(() => {
    const all = [];
    talentos.forEach((t) => all.push(...splitMulti(t.pais_residencia)));
    return dedupCi(all);
  }, [talentos]);

  const tiposCampana = useMemo(() => {
    const all = [];
    talentos.forEach((t) => all.push(...splitMulti(t.tipo_campana)));
    return dedupCi(all);
  }, [talentos]);

  // Match por PALABRA COMPLETA case-insensitive (regex \bneedle\b sobre el valor crudo).
  // Ej: filtro "UGC" matchea "UGC", "UGC Pack", "UGC / Reels", "Reels + UGC", "ugc, branding".
  // NO matchea "UGCX" ni "prUGC". Escapa metacaracteres regex del needle.
  const hasToken = (raw, needle) => {
    if (!raw || !needle) return false;
    const esc = String(needle).trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!esc) return false;
    try {
      return new RegExp(`(?:^|[^\\p{L}\\p{N}])${esc}(?:[^\\p{L}\\p{N}]|$)`, 'iu').test(String(raw));
    } catch (_e) {
      // Fallback sin unicode word boundaries (browsers viejos)
      return new RegExp(`(?:^|\\W)${esc}(?:\\W|$)`, 'i').test(String(raw));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return talentos.filter((t) => {
      if (visFilter !== '__ALL__' && (t.visibility || 'public') !== visFilter) return false;
      if (verticalFilter !== '__ALL__' && !hasToken(t.vertical, verticalFilter)) return false;
      if (paisFilter !== '__ALL__' && !hasToken(t.pais_residencia, paisFilter)) return false;
      if (tipoCampanaFilter !== '__ALL__' && !hasToken(t.tipo_campana, tipoCampanaFilter)) return false;
      if (existingIds.includes(t.id)) return false;
      if (!q) return true;
      const blob = [t.name, t.vertical, t.paises, t.pais_residencia, t.tipo_campana, t.descripcion].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [talentos, search, visFilter, verticalFilter, paisFilter, tipoCampanaFilter, existingIds]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const confirm = () => {
    const items = talentos.filter((t) => selected.has(t.id));
    onConfirm(items);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] bg-neutral-950 border-neutral-800 text-white overflow-hidden flex flex-col" data-testid="catalog-selector-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Catálogo de Talentos
          </DialogTitle>
          <p className="text-xs text-gray-500">Seleccioná los talentos que querés incluir en la propuesta</p>
        </DialogHeader>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 py-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, vertical, país..."
              className="pl-8 bg-neutral-900 border-neutral-700 text-white"
              data-testid="catalog-search"
            />
          </div>
          <Select value={visFilter} onValueChange={setVisFilter}>
            <SelectTrigger className="bg-neutral-900 border-neutral-700 text-white" data-testid="catalog-visibility-filter">
              <SelectValue placeholder="Visibilidad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">Todos (públicos + ocultos)</SelectItem>
              <SelectItem value="public">Solo públicos</SelectItem>
              <SelectItem value="hidden">Solo ocultos</SelectItem>
            </SelectContent>
          </Select>
          <Select value={verticalFilter} onValueChange={setVerticalFilter}>
            <SelectTrigger className="bg-neutral-900 border-neutral-700 text-white" data-testid="catalog-vertical-filter">
              <SelectValue placeholder="Vertical" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">Todas las verticales</SelectItem>
              {verticales.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={paisFilter} onValueChange={setPaisFilter}>
            <SelectTrigger className="bg-neutral-900 border-neutral-700 text-white" data-testid="catalog-pais-filter">
              <SelectValue placeholder="País residencia" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">Todos los países</SelectItem>
              {paisesResidencia.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tipoCampanaFilter} onValueChange={setTipoCampanaFilter}>
            <SelectTrigger className="bg-neutral-900 border-neutral-700 text-white" data-testid="catalog-tipo-campana-filter">
              <SelectValue placeholder="Tipo de campaña" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">Todos los tipos de campaña</SelectItem>
              {tiposCampana.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Grid */}
        <div className="overflow-y-auto flex-1 pr-1">
          {loading && <div className="text-center text-gray-500 py-10">Cargando catálogo...</div>}
          {!loading && filtered.length === 0 && <div className="text-center text-gray-500 py-10">Sin resultados</div>}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((t) => {
              const isSel = selected.has(t.id);
              const isHidden = (t.visibility || 'public') === 'hidden';
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggle(t.id)}
                  className="relative rounded-lg overflow-hidden border-2 transition-all text-left group"
                  style={{
                    borderColor: isSel ? '#F4A261' : '#262626',
                    backgroundColor: '#0f0f0f',
                  }}
                  data-testid={`catalog-card-${t.id}`}
                >
                  <div className="relative aspect-square bg-neutral-900">
                    {t.image ? (
                      <img src={t.image} alt={t.name} className="w-full h-full object-cover" style={{ filter: isSel ? 'none' : 'grayscale(0.4)' }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-700 text-xs">Sin foto</div>
                    )}
                    {isHidden && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider" style={{ backgroundColor: '#7C2D12dd', color: '#FED7AA' }}>
                        <EyeOff size={10} className="inline mr-1" />Oculto
                      </span>
                    )}
                    {isSel && (
                      <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">✓</div>
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <div className="text-sm font-semibold text-white truncate" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 0.5 }}>{t.name}</div>
                    {splitMulti(t.vertical).length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {splitMulti(t.vertical).slice(0, 3).map((v, i) => (
                          <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 font-medium">{v}</span>
                        ))}
                        {splitMulti(t.vertical).length > 3 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-neutral-800 text-gray-400">+{splitMulti(t.vertical).length - 3}</span>
                        )}
                      </div>
                    ) : (
                      <div className="text-[11px] text-gray-500 truncate">—</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter className="border-t border-neutral-800 pt-3">
          <div className="mr-auto text-sm text-gray-400" data-testid="catalog-selection-count">
            {selected.size} seleccionado{selected.size !== 1 ? 's' : ''}
          </div>
          <Button variant="outline" onClick={onClose} className="bg-neutral-900 border-neutral-700 text-white" data-testid="catalog-cancel">Cancelar</Button>
          <Button onClick={confirm} disabled={selected.size === 0} className="text-white" style={{ backgroundColor: '#F4A261' }} data-testid="catalog-confirm">
            Agregar a propuesta ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ CARGAR NUEVO TALENTO (desde flow comercial) ============
const NewTalentoDialog = ({ open, onClose, onCreated }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '', image: '', instagram_url: '', tiktok_url: '', youtube_url: '',
    vertical: '', descripcion: '', paises: '', edades: '', sexo: '',
    instagram_followers: '', tiktok_followers: '', visibility: 'public',
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) setForm({
      name: '', image: '', instagram_url: '', tiktok_url: '', youtube_url: '',
      vertical: '', descripcion: '', paises: '', edades: '', sexo: '',
      instagram_followers: '', tiktok_followers: '', visibility: 'public',
    });
  }, [open]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/commercial/upload', { method: 'POST', credentials: 'include', body: fd });
      const data = await res.json();
      if (res.ok && data.url) {
        setForm((f) => ({ ...f, image: data.url }));
      } else {
        toast({ title: 'Error', description: data.detail || 'No se pudo subir la imagen', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Fallo al subir imagen', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim() || !form.image) {
      toast({ title: 'Faltan datos', description: 'Nombre y foto son obligatorios', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await createCommercialTalento(form);
      toast({ title: 'Talento creado', description: `${form.name} agregado (${form.visibility === 'hidden' ? 'oculto' : 'público'})` });
      onCreated(res.talento);
      onClose();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'No se pudo crear', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl bg-neutral-950 border-neutral-800 text-white max-h-[90vh] overflow-y-auto" data-testid="new-talento-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Cargar Nuevo Talento</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Visibilidad *</Label>
            <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
              <SelectTrigger className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-visibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">🟢 Público — visible en el roster web</SelectItem>
                <SelectItem value="hidden">🔒 Oculto — solo visible para el equipo comercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nombre *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-name" />
          </div>
          <div>
            <Label>Verticales</Label>
            <Input value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} placeholder="Humor, Lifestyle" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-vertical" />
          </div>
          <div className="md:col-span-2">
            <Label>Foto *</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="file" ref={fileRef} onChange={handleFile} accept="image/*" className="hidden" />
              <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-neutral-800 border border-neutral-700 hover:bg-neutral-700" data-testid="new-talento-upload">
                <Upload size={14} className="mr-1" />{uploading ? 'Subiendo...' : 'Subir imagen'}
              </Button>
              {form.image && <img src={form.image} alt="preview" className="w-12 h-12 object-cover rounded" />}
            </div>
          </div>
          <div><Label>Instagram URL</Label><Input value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-ig-url" /></div>
          <div><Label>Instagram seguidores</Label><Input value={form.instagram_followers} onChange={(e) => setForm({ ...form, instagram_followers: e.target.value })} placeholder="150K" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-ig-fol" /></div>
          <div><Label>TikTok URL</Label><Input value={form.tiktok_url} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-tt-url" /></div>
          <div><Label>TikTok seguidores</Label><Input value={form.tiktok_followers} onChange={(e) => setForm({ ...form, tiktok_followers: e.target.value })} placeholder="89K" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-tt-fol" /></div>
          <div><Label>YouTube URL</Label><Input value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-yt-url" /></div>
          <div><Label>Países</Label><Input value={form.paises} onChange={(e) => setForm({ ...form, paises: e.target.value })} placeholder="Argentina, México" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-paises" /></div>
          <div><Label>Audiencia (Género)</Label><Input value={form.sexo} onChange={(e) => setForm({ ...form, sexo: e.target.value })} placeholder="Mujeres: 80%, Hombres: 20%" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-sexo" /></div>
          <div><Label>Edades</Label><Input value={form.edades} onChange={(e) => setForm({ ...form, edades: e.target.value })} placeholder="18-24 (45%), 25-34 (32%)" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-edades" /></div>
          <div className="md:col-span-2">
            <Label>Descripción</Label>
            <Textarea rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className="bg-neutral-900 border-neutral-700 mt-1" data-testid="new-talento-descripcion" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-neutral-900 border-neutral-700 text-white" data-testid="new-talento-cancel">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="text-white" style={{ backgroundColor: '#1B6B3A' }} data-testid="new-talento-save">
            {saving ? 'Guardando...' : 'Guardar talento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ AGREGAR TALENTO MANUAL A PROPUESTA (sin persistir en catálogo) ============
const ManualTalentoDialog = ({ open, onClose, onAdd }) => {
  const { toast } = useToast();
  const [f, setF] = useState({ nombre: '', foto_url: '', instagram_url: '', tiktok_url: '', youtube_url: '' });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) setF({ nombre: '', foto_url: '', instagram_url: '', tiktok_url: '', youtube_url: '' });
  }, [open]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/commercial/upload', { method: 'POST', credentials: 'include', body: fd });
      const d = await res.json();
      if (res.ok && d.url) setF({ ...f, foto_url: d.url });
      else toast({ title: 'Error', description: d.detail || 'No se pudo subir', variant: 'destructive' });
    } catch { /* ignore */ }
    finally { setUploading(false); }
  };

  const add = () => {
    if (!f.nombre.trim()) {
      toast({ title: 'Falta nombre', variant: 'destructive' });
      return;
    }
    onAdd({ ...f, es_manual: true, talento_id: null, items_precio: [] });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg bg-neutral-950 border-neutral-800 text-white" data-testid="manual-talento-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Agregar talento manual</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">Se agrega solo a esta propuesta (no se guarda en el catálogo). Los precios se cargan después en el editor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nombre *</Label>
            <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} className="bg-neutral-900 border-neutral-700 mt-1" data-testid="manual-nombre" />
          </div>
          <div>
            <Label>Foto</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="file" ref={fileRef} onChange={handleFile} accept="image/*" className="hidden" />
              <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-neutral-800 border border-neutral-700 hover:bg-neutral-700" data-testid="manual-upload">
                <Upload size={14} className="mr-1" />{uploading ? 'Subiendo...' : 'Subir imagen'}
              </Button>
              {f.foto_url && <img src={f.foto_url} alt="preview" className="w-12 h-12 object-cover rounded" />}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Instagram</Label><Input value={f.instagram_url} onChange={(e) => setF({ ...f, instagram_url: e.target.value })} className="bg-neutral-900 border-neutral-700 mt-1" data-testid="manual-ig" /></div>
            <div><Label className="text-xs">TikTok</Label><Input value={f.tiktok_url} onChange={(e) => setF({ ...f, tiktok_url: e.target.value })} className="bg-neutral-900 border-neutral-700 mt-1" data-testid="manual-tt" /></div>
            <div><Label className="text-xs">YouTube</Label><Input value={f.youtube_url} onChange={(e) => setF({ ...f, youtube_url: e.target.value })} className="bg-neutral-900 border-neutral-700 mt-1" data-testid="manual-yt" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-neutral-900 border-neutral-700 text-white" data-testid="manual-cancel">Cancelar</Button>
          <Button onClick={add} className="text-white" style={{ backgroundColor: '#F4A261' }} data-testid="manual-add">Agregar a propuesta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ EDITOR DE PRECIOS (tabla estructurada, múltiples items) ============
const PreciosEditor = ({ items, onChange, talentIdx }) => {
  const list = Array.isArray(items) ? items : [];
  const update = (idx, patch) => {
    const next = list.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };
  const addRow = () => onChange([...list, EMPTY_PRICE_ITEM()]);
  const removeRow = (idx) => onChange(list.filter((_, i) => i !== idx));

  return (
    <div className="pt-2" data-testid={`editor-precios-${talentIdx}`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-wider text-orange-400 font-bold">Presupuesto</Label>
        <Button
          type="button"
          size="sm"
          onClick={addRow}
          className="text-white h-7 px-2"
          style={{ backgroundColor: '#F4A261' }}
          data-testid={`editor-precio-add-${talentIdx}`}
        >
          <Plus size={12} className="mr-1" />Agregar
        </Button>
      </div>
      {list.length === 0 && (
        <div className="text-center text-xs text-gray-500 py-3 border border-dashed border-neutral-800 rounded" data-testid={`editor-precios-empty-${talentIdx}`}>
          Todavía no hay valores. Click en &quot;Agregar&quot; para crear el primero.
        </div>
      )}
      <div className="space-y-2">
        {list.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end bg-neutral-900/50 rounded-md p-2 border border-neutral-800" data-testid={`editor-precio-row-${talentIdx}-${i}`}>
            {/* RRSS */}
            <div className="col-span-12 md:col-span-2">
              <Label className="text-[10px] text-gray-500 uppercase">RRSS</Label>
              <Select value={it.red || '__NONE__'} onValueChange={(v) => update(i, { red: v === '__NONE__' ? '' : v })}>
                <SelectTrigger className="bg-neutral-900 border-neutral-700 h-9 text-sm" data-testid={`editor-precio-red-${talentIdx}-${i}`}>
                  <SelectValue placeholder="Elegir" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__NONE__">—</SelectItem>
                  {REDES_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Cantidad */}
            <div className="col-span-6 md:col-span-1">
              <Label className="text-[10px] text-gray-500 uppercase">Cant.</Label>
              <Input value={it.cantidad || ''} onChange={(e) => update(i, { cantidad: e.target.value })} placeholder="1" className="bg-neutral-900 border-neutral-700 h-9" data-testid={`editor-precio-cantidad-${talentIdx}-${i}`} />
            </div>
            {/* Tipo */}
            <div className="col-span-6 md:col-span-2">
              <Label className="text-[10px] text-gray-500 uppercase">Tipo</Label>
              <Select value={it.tipo || '__NONE__'} onValueChange={(v) => update(i, { tipo: v === '__NONE__' ? '' : v })}>
                <SelectTrigger className="bg-neutral-900 border-neutral-700 h-9 text-sm" data-testid={`editor-precio-tipo-${talentIdx}-${i}`}>
                  <SelectValue placeholder="Elegir" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__NONE__">—</SelectItem>
                  {TIPO_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Valor unitario */}
            <div className="col-span-6 md:col-span-2">
              <Label className="text-[10px] text-gray-500 uppercase">Valor unitario</Label>
              <Input value={it.precio || ''} onChange={(e) => update(i, { precio: e.target.value })} placeholder="$500 USD" className="bg-neutral-900 border-neutral-700 h-9" data-testid={`editor-precio-precio-${talentIdx}-${i}`} />
            </div>
            {/* Valor total (calculado automáticamente) */}
            <div className="col-span-6 md:col-span-2">
              <Label className="text-[10px] text-gray-500 uppercase">Valor total</Label>
              <div className="h-9 flex items-center px-3 rounded-md border border-neutral-800 bg-neutral-900/30 text-sm text-orange-400 font-semibold" data-testid={`editor-precio-total-${talentIdx}-${i}`}>
                {(() => {
                  const unit = parseFloat(String(it.precio || '').replace(/\./g, '').replace(/,/g, '.').match(/-?\d+(?:\.\d+)?/)?.[0] || '0');
                  const qty = parseFloat(String(it.cantidad || '1').replace(',', '.').match(/-?\d+(?:\.\d+)?/)?.[0] || '1');
                  const total = unit * qty;
                  return total > 0 ? '$' + total.toLocaleString('es-AR', { maximumFractionDigits: 2 }) : '—';
                })()}
              </div>
            </div>
            {/* Notas */}
            <div className="col-span-11 md:col-span-2">
              <Label className="text-[10px] text-gray-500 uppercase">Notas</Label>
              <Input value={it.notas || ''} onChange={(e) => update(i, { notas: e.target.value })} placeholder="Aprobación previa..." className="bg-neutral-900 border-neutral-700 h-9" data-testid={`editor-precio-notas-${talentIdx}-${i}`} />
            </div>
            {/* Trash */}
            <div className="col-span-1 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeRow(i)}
                className="text-red-400 hover:text-red-300 hover:bg-red-950/30 h-9 px-2"
                data-testid={`editor-precio-remove-${talentIdx}-${i}`}
                title="Eliminar"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ EDITOR DE PROPUESTA ============
// Estados nuevos: propuesta (default, inicial) | aprobada | no_aprobada
// (los legacy "borrador"/"enviada" se muestran como "propuesta" en la UI, se persisten como están hasta el próximo save)
const ESTADO_LABEL = {
  propuesta: 'Propuesta',
  aprobada: 'Aprobada',
  no_aprobada: 'No Aprobada',
  borrador: 'Propuesta',
  enviada: 'Propuesta',
};
const ESTADO_STYLE = {
  // active bg / text / label color
  propuesta:  { bg: '#F4A26122', border: '#F4A261', text: '#FDBA74', activeBg: '#F4A261', activeText: '#0a0a0a' },
  aprobada:   { bg: '#1B6B3A22', border: '#1B6B3A', text: '#4ADE80', activeBg: '#1B6B3A', activeText: '#ffffff' },
  no_aprobada:{ bg: '#7f1d1d22', border: '#B91C1C', text: '#F87171', activeBg: '#B91C1C', activeText: '#ffffff' },
};
// Normaliza estados legacy antes de comparar
const normEstado = (e) => {
  if (e === 'aprobada' || e === 'no_aprobada') return e;
  return 'propuesta';
};

// Componente de botones GRANDES para cambiar de estado. Se usa en el editor
// y en la card del listado de propuestas.
const EstadoButtons = ({ current, onChange, size = 'md', 'data-testid': dtid }) => {
  const active = normEstado(current);
  const options = [
    { key: 'propuesta',   label: 'PROPUESTA' },
    { key: 'aprobada',    label: 'APROBADA' },
    { key: 'no_aprobada', label: 'NO APROBADA' },
  ];
  const px = size === 'lg' ? 'px-5 py-3 text-sm' : size === 'sm' ? 'px-2.5 py-1 text-[10px]' : 'px-4 py-2.5 text-sm';
  return (
    <div className="inline-flex rounded-lg overflow-hidden border border-neutral-700 shadow-sm" data-testid={dtid || 'estado-buttons'} onClick={(e) => e.stopPropagation()}>
      {options.map((o) => {
        const st = ESTADO_STYLE[o.key];
        const isActive = active === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={(e) => { e.stopPropagation(); if (!isActive) onChange(o.key); }}
            className={`${px} font-bold uppercase tracking-wider transition-all whitespace-nowrap`}
            style={{
              backgroundColor: isActive ? st.activeBg : 'transparent',
              color: isActive ? st.activeText : st.text,
              borderRight: o.key !== 'no_aprobada' ? '1px solid #262626' : 'none',
              cursor: isActive ? 'default' : 'pointer',
              minWidth: size === 'lg' ? 120 : (size === 'sm' ? 78 : 116),
              letterSpacing: '0.06em',
            }}
            data-testid={`estado-btn-${o.key}${isActive ? '-active' : ''}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};


const PropuestaEditor = ({ propuestaId, onBack, onSaved }) => {
  const { toast } = useToast();
  const [propuesta, setPropuesta] = useState({
    id: null,
    cliente_nombre: '',
    comercial_nombre: '',
    agencia: '',
    contacto: '',
    marca: '',
    campana: '',
    temporalidad: '',
    scope: '',
    plazo_pago: '',
    notas: '',
    talentos: [],
    tipo: 'propuesta',  // 'propuesta' | 'acuerdo'
    estado: 'propuesta',  // 'propuesta' | 'aprobada' | 'no_aprobada'
    descuento_porcentaje: 0,
    fecha_creacion: new Date().toISOString(),
  });
  const [loading, setLoading] = useState(!!propuestaId);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [newTalentoOpen, setNewTalentoOpen] = useState(false);
  const previewRef = useRef(null);
  const [pdfVariant, setPdfVariant] = useState('full'); // 'full' | 'express'
  const [sharingWa, setSharingWa] = useState(false);
  const [copyingLink, setCopyingLink] = useState(false);
  const [publicLinkModal, setPublicLinkModal] = useState({ open: false, url: '', variant: 'full' });

  useEffect(() => {
    if (!propuestaId) return;
    setLoading(true);
    getPropuesta(propuestaId)
      .then((d) => setPropuesta({ ...d, talentos: d.talentos || [] }))
      .catch(() => toast({ title: 'Error', description: 'No se pudo cargar la propuesta', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [propuestaId, toast]);

  const addFromCatalog = (talentos) => {
    setPropuesta((p) => ({
      ...p,
      talentos: [
        ...p.talentos,
        ...talentos.map((t) => ({
          talento_id: t.id,
          es_manual: false,
          nombre: t.name,
          foto_url: t.image || '',
          instagram_url: t.instagram_url || '',
          tiktok_url: t.tiktok_url || '',
          youtube_url: t.youtube_url || '',
          instagram_followers: t.instagram_followers || '',
          tiktok_followers: t.tiktok_followers || '',
          youtube_subscribers: t.youtube_subscribers || '',
          vertical: t.vertical || '',
          descripcion: t.descripcion || '',
          paises: t.paises || '',
          edades: t.edades || '',
          sexo: t.sexo || '',
          precio: '',
          condiciones: '',
          items_precio: [],
        })),
      ],
    }));
  };

  const addManual = (item) => {
    setPropuesta((p) => ({ ...p, talentos: [...p.talentos, item] }));
  };

  const updateTalento = (idx, patch) => {
    setPropuesta((p) => ({
      ...p,
      talentos: p.talentos.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));
  };

  const removeTalento = (idx) => {
    setPropuesta((p) => ({ ...p, talentos: p.talentos.filter((_, i) => i !== idx) }));
  };

  // ==== Total en vivo (suma de items_precio * cantidad de todos los talentos) ====
  // Se recalcula en cada cambio del formulario. Muestra Subtotal, Descuento y Total Final.
  const liveTotals = useMemo(() => {
    let subtotal = 0;
    (propuesta.talentos || []).forEach((t) => {
      const items = Array.isArray(t.items_precio) ? t.items_precio : [];
      if (items.length > 0) {
        items.forEach((it) => { subtotal += parseValue(it.precio) * parseQty(it.cantidad); });
      } else if (t.precio) {
        subtotal += parseValue(t.precio);  // legacy fallback
      }
    });
    const pct = Math.max(0, Math.min(100, Number(propuesta.descuento_porcentaje) || 0));
    const descuento = subtotal * (pct / 100);
    const total = subtotal - descuento;
    return { subtotal, pct, descuento, total };
  }, [propuesta.talentos, propuesta.descuento_porcentaje]);

  // ==== Cambio de estado con auto-save ====
  const changeEstado = async (newEstado) => {
    if (!propuesta.id) {
      // Aún no guardada: sólo actualiza el state local, se persiste en el save.
      setPropuesta((p) => ({ ...p, estado: newEstado }));
      toast({ title: `Estado: ${ESTADO_LABEL[newEstado]}`, description: 'Se guardará al presionar "Guardar".' });
      return;
    }
    try {
      const res = await updatePropuesta(propuesta.id, { estado: newEstado });
      setPropuesta(res.propuesta);
      toast({ title: `Estado: ${ESTADO_LABEL[newEstado]}` });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'No se pudo cambiar el estado', variant: 'destructive' });
    }
  };

  const existingIds = propuesta.talentos.map((t) => t.talento_id).filter(Boolean);

  const save = async ({ propagate = true } = {}) => {
    const marca = (propuesta.marca || propuesta.cliente_nombre || '').trim();
    if (!marca || !propuesta.comercial_nombre.trim()) {
      toast({ title: 'Faltan datos', description: 'Marca y comercial son obligatorios', variant: 'destructive' });
      return null;
    }
    setSaving(true);
    try {
      const marca = (propuesta.marca || propuesta.cliente_nombre || '').trim();
      const payload = {
        cliente_nombre: marca,  // synced with marca for backwards compat & historial
        comercial_nombre: propuesta.comercial_nombre,
        tipo: propuesta.tipo === 'acuerdo' ? 'acuerdo' : 'propuesta',
        agencia: propuesta.agencia || '',
        contacto: propuesta.contacto || '',
        marca: marca,
        campana: propuesta.campana || '',
        temporalidad: propuesta.temporalidad || '',
        // Scope is now auto-computed from talentos + items_precio (see PropuestaPreview)
        scope: computeScopeFromTalentos(propuesta.talentos),
        plazo_pago: propuesta.plazo_pago || '',
        notas: propuesta.notas || '',
        talentos: propuesta.talentos,
        estado: propuesta.estado || 'propuesta',
        descuento_porcentaje: Number(propuesta.descuento_porcentaje) || 0,
      };
      if (propuesta.id) {
        const res = await updatePropuesta(propuesta.id, payload);
        setPropuesta(res.propuesta);
        toast({ title: 'Propuesta guardada' });
        return res.propuesta;
      } else {
        const res = await createPropuesta(payload);
        setPropuesta(res.propuesta);
        toast({ title: 'Propuesta creada' });
        // Only propagate to parent when the user hits "Guardar" directly.
        // When called from exportPDF we skip it so the parent doesn't change
        // `propuestaId`, which would remount the editor and null-out previewRef.
        if (propagate) onSaved?.(res.propuesta);
        return res.propuesta;
      }
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
      return null;
    } finally { setSaving(false); }
  };

  const exportPDF = async (variant = 'full') => {
    if (propuesta.talentos.length === 0) {
      toast({ title: 'Sin talentos', description: 'Agregá al menos un talento a la propuesta', variant: 'destructive' });
      return;
    }
    const marca = (propuesta.marca || propuesta.cliente_nombre || '').trim();
    if (!marca) {
      toast({ title: 'Falta marca', variant: 'destructive' });
      return;
    }
    setExporting(true);
    setPdfVariant(variant);
    try {
      // Save first (silently — don't bubble to parent to avoid unmount)
      if (!propuesta.id) {
        const saved = await save({ propagate: false });
        if (!saved) return;
      }
      // Give React a couple of frames to render the preview with fresh data + variant
      await new Promise((r) => setTimeout(r, 500));
      if (!previewRef.current) {
        throw new Error('El preview aún no está montado. Volvé a intentar en un segundo.');
      }
      const cli = marca.replace(/[^a-zA-Z0-9]+/g, '_');
      const date = new Date().toISOString().slice(0, 10);
      const suffix = variant === 'express' ? 'EXPRESS' : 'FULL';
      await generateProposalPDF(previewRef.current, {
        filename: `Propuesta_${suffix}_${cli}_${date}.pdf`,
        download: true,
      });
      toast({ title: `PDF ${suffix} generado`, description: 'La descarga inició' });
    } catch (e) {
      toast({ title: 'Error PDF', description: e.message || 'Fallo al generar', variant: 'destructive' });
    } finally { setExporting(false); }
  };

  const shareWhatsApp = async (variant = 'full') => {
    if (propuesta.talentos.length === 0) {
      toast({ title: 'Sin talentos', variant: 'destructive' });
      return;
    }
    const marca = (propuesta.marca || propuesta.cliente_nombre || '').trim();
    if (!marca) {
      toast({ title: 'Falta marca', variant: 'destructive' });
      return;
    }
    setSharingWa(true);
    setPdfVariant(variant);
    try {
      if (!propuesta.id) {
        const saved = await save({ propagate: false });
        if (!saved) return;
      }
      await new Promise((r) => setTimeout(r, 500));
      if (!previewRef.current) throw new Error('Preview no montado');
      // Generate PDF blob (no download)
      const cli = marca.replace(/[^a-zA-Z0-9]+/g, '_');
      const date = new Date().toISOString().slice(0, 10);
      const suffix = variant === 'express' ? 'EXPRESS' : 'FULL';
      const filename = `Propuesta_${suffix}_${cli}_${date}.pdf`;
      const blob = await generateProposalPDF(previewRef.current, { filename, download: false });
      // Upload to backend
      const fd = new FormData();
      fd.append('file', blob, filename);
      const uploadRes = await fetch('/api/commercial/propuesta-pdf', {
        method: 'POST', credentials: 'include', body: fd,
      });
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok || !uploadData.url) {
        throw new Error(uploadData.detail || 'No se pudo subir el PDF');
      }
      const publicUrl = `${window.location.origin}${uploadData.url}`;
      const text = encodeURIComponent(
        `Hola, te comparto la propuesta de ${marca}` +
        (propuesta.campana ? ` — ${propuesta.campana}` : '') +
        `:\n${publicUrl}`
      );
      window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
      toast({ title: 'Listo para enviar', description: 'Se abrió WhatsApp con el link a la propuesta' });
    } catch (e) {
      toast({ title: 'Error WhatsApp', description: e.message || 'Fallo al compartir', variant: 'destructive' });
    } finally { setSharingWa(false); }
  };

  const copyPublicLink = async (variant = 'full') => {
    if (propuesta.talentos.length === 0) {
      toast({ title: 'Sin talentos', variant: 'destructive' });
      return;
    }
    const marca = (propuesta.marca || propuesta.cliente_nombre || '').trim();
    if (!marca) {
      toast({ title: 'Falta marca', variant: 'destructive' });
      return;
    }
    setCopyingLink(true);
    setPdfVariant(variant);
    try {
      if (!propuesta.id) {
        const saved = await save({ propagate: false });
        if (!saved) return;
      }
      await new Promise((r) => setTimeout(r, 500));
      if (!previewRef.current) throw new Error('Preview no montado');
      const cli = marca.replace(/[^a-zA-Z0-9]+/g, '_');
      const date = new Date().toISOString().slice(0, 10);
      const suffix = variant === 'express' ? 'EXPRESS' : 'FULL';
      const filename = `Propuesta_${suffix}_${cli}_${date}.pdf`;
      const blob = await generateProposalPDF(previewRef.current, { filename, download: false });
      const fd = new FormData();
      fd.append('file', blob, filename);
      const uploadRes = await fetch('/api/commercial/propuesta-pdf', {
        method: 'POST', credentials: 'include', body: fd,
      });
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok || !uploadData.url) {
        throw new Error(uploadData.detail || 'No se pudo subir el PDF');
      }
      const publicUrl = `${window.location.origin}${uploadData.url}`;
      // Try to copy to clipboard (may fail in insecure contexts / older browsers)
      let copied = false;
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(publicUrl);
          copied = true;
        }
      } catch { copied = false; }
      setPublicLinkModal({ open: true, url: publicUrl, variant });
      toast({
        title: copied ? 'Link copiado' : 'Link generado',
        description: copied ? 'Pegalo donde quieras compartirlo' : 'Copialo manualmente desde la ventana',
      });
    } catch (e) {
      toast({ title: 'Error link público', description: e.message || 'Fallo al generar', variant: 'destructive' });
    } finally { setCopyingLink(false); }
  };

  if (loading) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center text-gray-500" style={{ backgroundColor: '#0a0a0a' }}>
          Cargando propuesta...
        </div>
        {/* Keep the preview mounted even during loading so previewRef stays valid */}
        <PropuestaPreview ref={previewRef} propuesta={propuesta} variant={pdfVariant} />
      </>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Header */}
      <div className="sticky top-0 z-30" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="border-b border-neutral-800 px-6 py-4 flex flex-wrap items-center justify-between gap-3" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-gray-400 hover:text-white hover:bg-neutral-900" data-testid="editor-back">
            <ArrowLeft size={14} className="mr-1" />Volver
          </Button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {propuesta.id ? 'Editar Propuesta' : 'Nueva Propuesta'}
          </h1>
          {/* Estado (Propuesta / Aprobada / No Aprobada) — cambia en cualquier momento */}
          <EstadoButtons current={propuesta.estado} onChange={changeEstado} size="md" data-testid="editor-estado-buttons" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setNewTalentoOpen(true)} className="bg-neutral-900 border-neutral-700 text-white hover:bg-neutral-800" data-testid="editor-new-talento-btn">
            <Plus size={14} className="mr-1" />Talento al catálogo
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="text-white" style={{ backgroundColor: '#1B6B3A' }} data-testid="editor-save">
            <Save size={14} className="mr-1" />{saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <div className="h-6 w-px bg-neutral-700 mx-1" aria-hidden />
          {/* Botones de exportación — mismo tamaño uniforme y mismo color por par (Full/Express) */}
          {/* PDF FULL / EXPRESS → naranja Talengers */}
          <Button size="sm" onClick={() => exportPDF('full')} disabled={exporting || sharingWa} className="text-white font-semibold justify-center min-w-[120px]" style={{ backgroundColor: '#F4A261' }} data-testid="editor-export-pdf-full">
            <Download size={14} className="mr-1.5" />{exporting && pdfVariant === 'full' ? '...' : 'PDF Full'}
          </Button>
          <Button size="sm" onClick={() => exportPDF('express')} disabled={exporting || sharingWa} className="text-white font-semibold justify-center min-w-[120px]" style={{ backgroundColor: '#F4A261' }} data-testid="editor-export-pdf-express">
            <Zap size={14} className="mr-1.5" />{exporting && pdfVariant === 'express' ? '...' : 'PDF Express'}
          </Button>
          {/* WhatsApp FULL / EXPRESS → verde WhatsApp */}
          <Button size="sm" onClick={() => shareWhatsApp('full')} disabled={exporting || sharingWa} className="text-white font-semibold justify-center min-w-[120px]" style={{ backgroundColor: '#25D366' }} data-testid="editor-wa-full" title="Enviar propuesta FULL por WhatsApp">
            <MessageCircle size={14} className="mr-1.5" />{sharingWa && pdfVariant === 'full' ? '...' : 'WA Full'}
          </Button>
          <Button size="sm" onClick={() => shareWhatsApp('express')} disabled={exporting || sharingWa} className="text-white font-semibold justify-center min-w-[120px]" style={{ backgroundColor: '#25D366' }} data-testid="editor-wa-express" title="Enviar propuesta EXPRESS por WhatsApp">
            <Zap size={14} className="mr-1.5" />{sharingWa && pdfVariant === 'express' ? '...' : 'WA Express'}
          </Button>
          {/* Link público FULL / EXPRESS → violeta */}
          <Button size="sm" onClick={() => copyPublicLink('full')} disabled={exporting || sharingWa || copyingLink} className="text-white font-semibold justify-center min-w-[120px]" style={{ backgroundColor: '#7c3aed' }} data-testid="editor-link-full" title="Copiar link público del PDF FULL">
            <Link2 size={14} className="mr-1.5" />{copyingLink && pdfVariant === 'full' ? '...' : 'Link Full'}
          </Button>
          <Button size="sm" onClick={() => copyPublicLink('express')} disabled={exporting || sharingWa || copyingLink} className="text-white font-semibold justify-center min-w-[120px]" style={{ backgroundColor: '#7c3aed' }} data-testid="editor-link-express" title="Copiar link público del PDF EXPRESS">
            <Zap size={14} className="mr-1.5" />{copyingLink && pdfVariant === 'express' ? '...' : 'Link Express'}
          </Button>
        </div>
      </div>

      {/* Sticky Live Total — refleja subtotal / descuento / total en tiempo real */}
      <div className="border-b border-neutral-800 px-6 py-3" style={{ backgroundColor: '#0a0a0a', backgroundImage: 'linear-gradient(to right, #0a0a0a, #1a0f2e 40%, #0a0a0a)' }} data-testid="live-total-bar">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-violet-300 font-bold">
            <span className="inline-block w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
            Total en vivo
          </div>
          <div className="flex items-center gap-8 flex-wrap">
            <div className="flex flex-col items-end">
              <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Subtotal</span>
              <span className="text-xl font-bold" style={{ color: '#FED7AA' }} data-testid="live-subtotal">{formatValue(liveTotals.subtotal)}</span>
            </div>
            {liveTotals.pct > 0 && (
              <div className="flex flex-col items-end">
                <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Descuento {liveTotals.pct}%</span>
                <span className="text-xl font-bold" style={{ color: '#FDBA74' }} data-testid="live-descuento">−{formatValue(liveTotals.descuento)}</span>
              </div>
            )}
            <div className="flex flex-col items-end">
              <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Total Final</span>
              <span className="text-xl font-bold" style={{ color: '#F4A261' }} data-testid="live-total">{formatValue(liveTotals.total)}</span>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Meta form — datos de la propuesta y de la campaña */}
      <div className="px-6 py-4 border-b border-neutral-900 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Marca *</Label>
            <Input
              value={propuesta.marca || propuesta.cliente_nombre || ''}
              onChange={(e) => setPropuesta({
                ...propuesta,
                marca: e.target.value,
                cliente_nombre: e.target.value,  // sync for historial/filters
              })}
              placeholder="Ej: Coca Cola Zero"
              className="bg-neutral-900 border-neutral-700 mt-1"
              data-testid="editor-marca-main"
            />
          </div>
          <div>
            <Label>Comercial Talengers *</Label>
            <Input value={propuesta.comercial_nombre} onChange={(e) => setPropuesta({ ...propuesta, comercial_nombre: e.target.value })} placeholder="Ej: Juan Pérez" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="editor-comercial" />
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-orange-400 font-bold mb-2">Datos de la campaña</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label>Agencia</Label>
              <Input value={propuesta.agencia || ''} onChange={(e) => setPropuesta({ ...propuesta, agencia: e.target.value })} placeholder="Wunderman" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="editor-agencia" />
            </div>
            <div>
              <Label>Contacto</Label>
              <Input value={propuesta.contacto || ''} onChange={(e) => setPropuesta({ ...propuesta, contacto: e.target.value })} placeholder="María Gómez" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="editor-contacto" />
            </div>
            <div>
              <Label>Campaña</Label>
              <Input value={propuesta.campana || ''} onChange={(e) => setPropuesta({ ...propuesta, campana: e.target.value })} placeholder="Verano 2026" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="editor-campana" />
            </div>
            <div>
              <Label>Temporalidad</Label>
              <Input value={propuesta.temporalidad || ''} onChange={(e) => setPropuesta({ ...propuesta, temporalidad: e.target.value })} placeholder="Enero-Marzo 2026" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="editor-temporalidad" />
            </div>
            <div>
              <Label>Plazo de pago</Label>
              <Input value={propuesta.plazo_pago || ''} onChange={(e) => setPropuesta({ ...propuesta, plazo_pago: e.target.value })} placeholder="30 días fecha factura" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="editor-plazo-pago" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-violet-400 font-bold">Descuento paquete completo (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={propuesta.descuento_porcentaje || ''}
                onChange={(e) => setPropuesta({ ...propuesta, descuento_porcentaje: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                placeholder="Ej: 10"
                className="bg-neutral-900 border-neutral-700 mt-1"
                data-testid="editor-descuento"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-orange-400 font-bold">Scope (auto-generado)</Label>
              <div className="mt-1 min-h-[38px] px-3 py-2 rounded-md border border-neutral-800 bg-neutral-900/40 text-sm text-gray-200" data-testid="editor-scope-auto">
                {computeScopeFromTalentos(propuesta.talentos) || <span className="text-gray-500 italic">Se arma automáticamente con los talentos e ítems que cargues abajo.</span>}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Notas internas (opcional)</Label>
              <Input value={propuesta.notas || ''} onChange={(e) => setPropuesta({ ...propuesta, notas: e.target.value })} placeholder="No sale en el PDF" className="bg-neutral-900 border-neutral-700 mt-1" data-testid="editor-notas" />
            </div>
          </div>
        </div>
      </div>

      {/* Actions bar */}
      <div className="px-6 py-3 flex flex-wrap gap-2 border-b border-neutral-900">
        <Button onClick={() => setCatalogOpen(true)} className="text-white font-semibold" style={{ backgroundColor: '#F4A261' }} data-testid="editor-add-from-catalog">
          <Plus size={14} className="mr-1" />Agregar desde catálogo
        </Button>
        <Button onClick={() => setManualOpen(true)} variant="outline" className="bg-neutral-900 border-neutral-700 text-white hover:bg-neutral-800" data-testid="editor-add-manual">
          <Plus size={14} className="mr-1" />Cargar manual
        </Button>
        <div className="ml-auto text-sm text-gray-400 flex items-center" data-testid="editor-talents-count">
          {propuesta.talentos.length} talento{propuesta.talentos.length !== 1 ? 's' : ''} en la propuesta
        </div>
      </div>

      {/* Talents list */}
      <div className="p-6 space-y-4">
        {propuesta.talentos.length === 0 && (
          <div className="text-center text-gray-500 py-16 border border-dashed border-neutral-800 rounded-lg" data-testid="editor-empty">
            No hay talentos aún. Agregá desde el catálogo o cargalos manualmente.
          </div>
        )}
        {propuesta.talentos.map((t, i) => (
          <div key={i} className="rounded-lg border border-neutral-800 bg-neutral-950 overflow-hidden" data-testid={`editor-talent-row-${i}`}>
            <div className="flex flex-col md:flex-row">
              <div className="md:w-48 md:h-48 h-40 relative bg-neutral-900 flex-shrink-0">
                {t.foto_url ? (
                  <img src={t.foto_url} alt={t.nombre} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-700">Sin foto</div>
                )}
                {t.es_manual && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider" style={{ backgroundColor: '#F4A26166', color: '#FDBA74' }}>
                    Manual
                  </span>
                )}
              </div>
              <div className="flex-1 p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 0.5 }}>{t.nombre}</div>
                    {t.vertical && <div className="text-xs text-gray-500">{t.vertical}</div>}
                    <div className="text-xs text-gray-500 mt-1 flex gap-3">
                      {t.instagram_followers && <span>IG: {t.instagram_followers}</span>}
                      {t.tiktok_followers && <span>TT: {t.tiktok_followers}</span>}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeTalento(i)} className="text-red-400 hover:text-red-300 hover:bg-red-950/30" data-testid={`editor-talent-remove-${i}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
                {/* Redes URL edit (allow override for manual, view for catalog) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  <Input value={t.instagram_url || ''} onChange={(e) => updateTalento(i, { instagram_url: e.target.value })} placeholder="Instagram URL" className="bg-neutral-900 border-neutral-700 text-xs h-8" data-testid={`editor-t-ig-${i}`} />
                  <Input value={t.tiktok_url || ''} onChange={(e) => updateTalento(i, { tiktok_url: e.target.value })} placeholder="TikTok URL" className="bg-neutral-900 border-neutral-700 text-xs h-8" data-testid={`editor-t-tt-${i}`} />
                  <Input value={t.youtube_url || ''} onChange={(e) => updateTalento(i, { youtube_url: e.target.value })} placeholder="YouTube URL" className="bg-neutral-900 border-neutral-700 text-xs h-8" data-testid={`editor-t-yt-${i}`} />
                </div>
                {/* Precios estructurados (múltiples items) */}
                <PreciosEditor
                  items={(t.items_precio && t.items_precio.length > 0) ? t.items_precio :
                        (t.precio || t.condiciones) ? [{ red: '', cantidad: '', tipo: '', precio: t.precio || '', notas: t.condiciones || '' }] : []}
                  onChange={(items) => updateTalento(i, { items_precio: items, precio: '', condiciones: '' })}
                  talentIdx={i}
                />
                {/* Aclaraciones del comercial (descuentos, condiciones especiales, etc.) */}
                <div className="pt-2">
                  <Label className="text-xs uppercase tracking-wider text-orange-400 font-bold">Aclaraciones</Label>
                  <Textarea
                    rows={2}
                    value={t.aclaraciones || ''}
                    onChange={(e) => updateTalento(i, { aclaraciones: e.target.value })}
                    placeholder="Ej: 10% descuento por contratación del pack completo · precios sujetos a cambios..."
                    className="bg-neutral-900 border-neutral-700 mt-1 text-sm"
                    data-testid={`editor-aclaraciones-${i}`}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dialogs */}
      <CatalogSelector open={catalogOpen} onClose={() => setCatalogOpen(false)} onConfirm={addFromCatalog} existingIds={existingIds} />
      <ManualTalentoDialog open={manualOpen} onClose={() => setManualOpen(false)} onAdd={addManual} />
      <NewTalentoDialog open={newTalentoOpen} onClose={() => setNewTalentoOpen(false)} onCreated={() => toast({ title: 'Talento creado en catálogo' })} />

      {/* Link público modal */}
      <Dialog open={publicLinkModal.open} onOpenChange={(o) => setPublicLinkModal((m) => ({ ...m, open: o }))}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-lg" data-testid="public-link-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Link2 size={18} className="text-violet-400" /> Link público — {publicLinkModal.variant === 'express' ? 'PDF Express' : 'PDF Full'}</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">
              Cualquier persona con este link podrá abrir el PDF. No requiere login. Copialo y compartilo por el canal que quieras.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={publicLinkModal.url} className="bg-neutral-900 border-neutral-700 text-white text-sm" data-testid="public-link-input" onFocus={(e) => e.target.select()} />
            <Button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(publicLinkModal.url);
                  toast({ title: 'Link copiado' });
                } catch { toast({ title: 'No se pudo copiar', description: 'Copialo manualmente', variant: 'destructive' }); }
              }}
              className="text-white font-semibold flex-shrink-0"
              style={{ backgroundColor: '#7c3aed' }}
              data-testid="public-link-copy"
            >
              <Copy size={14} className="mr-1" />Copiar
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => window.open(publicLinkModal.url, '_blank', 'noopener,noreferrer')} className="bg-neutral-900 border-neutral-700 text-white hover:bg-neutral-800" data-testid="public-link-open">
              Abrir PDF
            </Button>
            <Button onClick={() => setPublicLinkModal((m) => ({ ...m, open: false }))} className="bg-neutral-800 text-white hover:bg-neutral-700" data-testid="public-link-close">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden preview for PDF */}
      <PropuestaPreview ref={previewRef} propuesta={propuesta} variant={pdfVariant} />
    </div>
  );
};

// ============ LISTADO / HISTORIAL ============
const PropuestasList = ({ onOpenEditor, onLogout }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comercialFilter, setComercialFilter] = useState('');
  const [marcaFilter, setMarcaFilter] = useState('');
  const [agenciaFilter, setAgenciaFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('__ALL__');
  const [newTalentoOpen, setNewTalentoOpen] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (comercialFilter) params.comercial = comercialFilter;
      if (marcaFilter) params.marca = marcaFilter;
      if (agenciaFilter) params.agencia = agenciaFilter;
      if (estadoFilter !== '__ALL__') params.estado = estadoFilter;
      const d = await listPropuestas(params);
      setItems(d.propuestas || []);
    } catch (e) {
      if (e?.response?.status === 401) { onLogout(); return; }
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [comercialFilter, marcaFilter, agenciaFilter, estadoFilter, onLogout]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar propuesta "${p.cliente_nombre}"?`)) return;
    try {
      await deletePropuesta(p.id);
      setItems((arr) => arr.filter((x) => x.id !== p.id));
    } catch { toast({ title: 'Error al eliminar', variant: 'destructive' }); }
  };

  const duplicate = async (p) => {
    try {
      // Load the full propuesta (list endpoint may return trimmed cards)
      const full = await getPropuesta(p.id);
      const src = full.propuesta || full;
      const baseMarca = src.marca || src.cliente_nombre || 'Propuesta';
      const nuevaMarca = `${baseMarca} (copia)`;
      const payload = {
        cliente_nombre: nuevaMarca,
        comercial_nombre: src.comercial_nombre || '',
        tipo: src.tipo === 'acuerdo' ? 'acuerdo' : 'propuesta',
        agencia: src.agencia || '',
        contacto: src.contacto || '',
        marca: nuevaMarca,
        campana: src.campana || '',
        temporalidad: src.temporalidad || '',
        scope: src.scope || '',
        plazo_pago: src.plazo_pago || '',
        notas: src.notas || '',
        estado: 'propuesta',
        descuento_porcentaje: Number(src.descuento_porcentaje) || 0,
        talentos: (src.talentos || []).map((t) => ({ ...t })),
      };
      const res = await createPropuesta(payload);
      toast({ title: 'Propuesta duplicada', description: `"${nuevaMarca}" creada como Propuesta` });
      // Open the new duplicate in the editor
      if (res?.propuesta?.id) onOpenEditor(res.propuesta.id);
      else fetchItems();
    } catch (e) {
      toast({ title: 'Error al duplicar', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const changeEstadoInList = async (id, newEstado) => {
    try {
      await updatePropuesta(id, { estado: newEstado });
      setItems((arr) => arr.map((p) => (p.id === id ? { ...p, estado: newEstado } : p)));
      toast({ title: `Estado: ${ESTADO_LABEL[newEstado]}` });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'No se pudo cambiar el estado', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="border-b border-neutral-800 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Propuestas Comerciales</h1>
          <p className="text-xs text-gray-500">Historial · filtros por cliente y comercial · genera PDF</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setNewTalentoOpen(true)} className="bg-neutral-900 border-neutral-700 text-white hover:bg-neutral-800" data-testid="propuestas-new-talento-btn">
            <Plus size={14} className="mr-1" />Nuevo talento
          </Button>
          <Button size="sm" onClick={() => onOpenEditor(null)} className="text-white font-semibold" style={{ backgroundColor: '#F4A261' }} data-testid="propuestas-new-btn">
            <Plus size={14} className="mr-1" />Nueva propuesta
          </Button>
          <BackToPanelButton size="sm" className="bg-neutral-900 text-white" />
          <Button variant="outline" size="sm" onClick={onLogout} className="bg-neutral-900 border-neutral-700 text-white hover:bg-neutral-800" data-testid="propuestas-logout">
            <LogOut size={14} className="mr-1" />Salir
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-5 gap-3 border-b border-neutral-900">
        <Input value={marcaFilter} onChange={(e) => setMarcaFilter(e.target.value)} placeholder="Filtrar por marca..." className="bg-neutral-900 border-neutral-700 text-white" data-testid="filter-marca" />
        <Input value={agenciaFilter} onChange={(e) => setAgenciaFilter(e.target.value)} placeholder="Filtrar por agencia..." className="bg-neutral-900 border-neutral-700 text-white" data-testid="filter-agencia" />
        <Input value={comercialFilter} onChange={(e) => setComercialFilter(e.target.value)} placeholder="Filtrar por comercial..." className="bg-neutral-900 border-neutral-700 text-white" data-testid="filter-comercial" />
        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger className="bg-neutral-900 border-neutral-700 text-white" data-testid="filter-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__ALL__">Todos los estados</SelectItem>
            <SelectItem value="propuesta">Propuesta</SelectItem>
            <SelectItem value="aprobada">Aprobada</SelectItem>
            <SelectItem value="no_aprobada">No Aprobada</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={fetchItems} variant="outline" className="bg-neutral-900 border-neutral-700 text-white hover:bg-neutral-800" data-testid="filter-refresh">
          <RefreshCw size={14} className="mr-1" />Actualizar
        </Button>
      </div>

      {error && (
        <div className="mx-6 mt-4 rounded-lg border border-red-700/60 bg-red-900/30 p-4 text-red-200 text-sm" data-testid="propuestas-error">
          Error: {error}
        </div>
      )}

      <div className="px-6 pb-12 pt-4">
        {loading && <div className="text-center text-gray-500 py-10">Cargando...</div>}
        {!loading && items.length === 0 && !error && (
          <div className="text-center text-gray-500 py-16 border border-dashed border-neutral-800 rounded-lg" data-testid="propuestas-empty">
            No hay propuestas. Creá la primera con &quot;Nueva propuesta&quot;.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div
              key={p.id}
              onClick={() => onOpenEditor(p.id)}
              className="rounded-lg border border-neutral-800 bg-neutral-950 p-4 cursor-pointer hover:border-orange-500/50 transition-all"
              data-testid={`propuesta-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xl font-bold truncate" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 0.5 }}>
                    {p.cliente_nombre}
                  </div>
                  <div className="text-xs text-gray-500 truncate">Comercial: {p.comercial_nombre}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); duplicate(p); }} className="text-sky-400 hover:text-sky-300 hover:bg-sky-950/30 flex-shrink-0" data-testid={`propuesta-duplicate-${p.id}`} title="Duplicar propuesta">
                  <Copy size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); remove(p); }} className="text-red-400 hover:text-red-300 hover:bg-red-950/30 flex-shrink-0" data-testid={`propuesta-delete-${p.id}`} title="Eliminar propuesta">
                  <Trash2 size={14} />
                </Button>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-400 flex-wrap">
                <span className="px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider" style={{
                  backgroundColor: (p.tipo === 'acuerdo') ? '#1B6B3A33' : '#7c3aed33',
                  color: (p.tipo === 'acuerdo') ? '#4ADE80' : '#c4b5fd',
                }} data-testid={`propuesta-tipo-${p.id}`}>
                  {p.tipo === 'acuerdo' ? 'Acuerdo' : 'Propuesta'}
                </span>
                <span>{(p.talentos?.length || 0)} talentos</span>
                <span className="ml-auto">{new Date(p.fecha_creacion).toLocaleDateString('es-AR')}</span>
              </div>
              {/* Estado buttons — cambian directo desde la lista sin abrir el editor */}
              <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                <EstadoButtons
                  current={p.estado}
                  onChange={(newEstado) => changeEstadoInList(p.id, newEstado)}
                  size="sm"
                  data-testid={`propuesta-estado-buttons-${p.id}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <NewTalentoDialog open={newTalentoOpen} onClose={() => setNewTalentoOpen(false)} onCreated={() => toast({ title: 'Talento creado' })} />
    </div>
  );
};

// ============ ROOT ============
const PropuestasApp = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        await commercialMe();
        if (!cancel) setAuthed(true);
      } catch { /* not authed */ }
      finally { if (!cancel) setChecking(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const logout = async () => {
    try { await commercialLogout(); } catch { /* ignore */ }
    // El usuario puede haber entrado vía SSO (cookie /influs). No cerramos esa sesión,
    // sólo lo devolvemos al hub principal de /influs. Si estaba con sesión comercial
    // dedicada, ya la borramos arriba y /influs le pedirá su propio password.
    navigate('/panel/influs');
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-gray-500" style={{ backgroundColor: '#0a0a0a' }}>Cargando...</div>;
  if (!authed) {
    // Session expired or never authenticated. If user came via /panel,
    // bounce them back to the unified Hub instead of the local login form.
    if (redirectToHubIfFromHub()) return null;
    return <CommercialLogin onAuthed={() => setAuthed(true)} onBack={() => navigate('/panel')} />;
  }

  if (view === 'editor') {
    return (
      <PropuestaEditor
        propuestaId={editingId}
        onBack={() => { setView('list'); setEditingId(null); }}
        onSaved={(p) => { setEditingId(p.id); }}
      />
    );
  }

  return (
    <PropuestasList
      onOpenEditor={(id) => { setEditingId(id); setView('editor'); }}
      onLogout={logout}
    />
  );
};

export default PropuestasApp;
